import React, { useEffect, useState } from "react";
import { Search, Download, Eye, Award, Trophy, Clock, LogIn, BookOpen } from "lucide-react";
import { api, fileUrl, API } from "@/lib/apiClient";
import { PageHeader, Loader, EmptyState, StatusBadge } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function AdminStudents() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const q = new URLSearchParams({ page, limit: 20 });
    if (search) q.set("search", search);
    if (status !== "all") q.set("status", status);
    const { data } = await api.get(`/admin/students?${q}`);
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, status]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 400); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const openDetail = async (id) => { const { data } = await api.get(`/admin/students/${id}`); setDetail(data); };
  const toggleActive = async (id, v) => { await api.patch(`/admin/students/${id}`, { is_active: v }); load(); toast.success("Updated"); };

  const exportCsv = async () => {
    const res = await api.get("/admin/students-export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "students.csv"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PageHeader title="Students" subtitle="Search, filter, and manage students"
        action={<Button variant="outline" className="rounded-full" onClick={exportCsv} data-testid="export-csv"><Download className="w-4 h-4 mr-1.5" /> Export CSV</Button>} />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search by name or email" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="student-search" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="sm:w-48" data-testid="student-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All students</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!data ? <Loader /> : data.items.length === 0 ? (
        <EmptyState icon={Search} title="No students found" description="No students match your search yet." testId="students-empty" />
      ) : (
        <>
          <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-left"><tr>
                <th className="p-3 font-medium">Name</th><th className="p-3 font-medium hidden sm:table-cell">Niche</th>
                <th className="p-3 font-medium hidden md:table-cell">Status</th><th className="p-3 font-medium text-right">Actions</th>
              </tr></thead>
              <tbody>
                {data.items.map((s) => (
                  <tr key={s.id} className="border-t border-border" data-testid={`student-row-${s.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {s.photo_url ? <img src={fileUrl(s.photo_url)} className="w-8 h-8 rounded-full object-cover" alt="" /> : <div className="w-8 h-8 rounded-full bg-teal/10 text-teal flex items-center justify-center text-xs font-bold">{s.name?.[0]}</div>}
                        <div><p className="font-medium">{s.name}</p><p className="text-xs text-muted-foreground">{s.email}</p></div>
                      </div>
                    </td>
                    <td className="p-3 hidden sm:table-cell text-muted-foreground">{s.niche || "—"}</td>
                    <td className="p-3 hidden md:table-cell"><StatusBadge status={s.is_active ? "approved" : "rejected"} /></td>
                    <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={() => openDetail(s.id)} data-testid={`view-student-${s.id}`}><Eye className="w-4 h-4" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button variant="outline" size="sm" className="rounded-full" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <span className="text-sm text-muted-foreground">Page {page} of {data.pages || 1} · {data.total} total</span>
            <Button variant="outline" size="sm" className="rounded-full" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle>{detail.name}</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  {detail.profile?.photo_url ? <img src={fileUrl(detail.profile.photo_url)} className="w-16 h-16 rounded-full object-cover" alt="" /> : <div className="w-16 h-16 rounded-full bg-teal/10 text-teal flex items-center justify-center text-xl font-bold">{detail.name?.[0]}</div>}
                  <div>
                    <p className="text-muted-foreground">{detail.email}</p>
                    <p>{detail.profile?.niche} {detail.profile?.country && `· ${detail.profile.country}`}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-secondary/50">
                  <span>Account active</span>
                  <Switch checked={detail.is_active} onCheckedChange={(v) => { toggleActive(detail.id, v); setDetail((d) => ({ ...d, is_active: v })); }} data-testid="toggle-active" />
                </div>
                {detail.profile?.skills?.length > 0 && <p><b>Skills:</b> {detail.profile.skills.join(", ")}</p>}
                {detail.profile?.linkedin && <p><b>LinkedIn:</b> <a className="text-teal" href={detail.profile.linkedin} target="_blank" rel="noreferrer">{detail.profile.linkedin}</a></p>}
                {detail.profile?.upwork && <p><b>Upwork:</b> <a className="text-teal" href={detail.profile.upwork} target="_blank" rel="noreferrer">{detail.profile.upwork}</a></p>}
                <div>
                  <p className="font-semibold mb-1">Progress</p>
                  {detail.progress?.map((p) => (
                    <div key={p.phase_id} className="flex items-center justify-between py-1 border-b border-border/50 last:border-0">
                      <span>{p.title}</span>
                      <span className="text-muted-foreground text-xs">{p.completed_lessons}/{p.total_lessons} · {p.evidence_status}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="font-semibold mb-1 flex items-center gap-1.5"><Award className="w-4 h-4 text-teal" /> Badges Earned</p>
                  {detail.badges?.length > 0 ? (
                    <div className="flex flex-wrap gap-2" data-testid="student-badges">
                      {detail.badges.map((b) => {
                        const BI = { "log-in": LogIn, "clock": Clock, "book-open": BookOpen, "trophy": Trophy }[b.icon] || Award;
                        return <span key={b.id} className="inline-flex items-center gap-1.5 text-xs font-medium bg-teal/10 text-teal px-2.5 py-1 rounded-full"><BI className="w-3.5 h-3.5" /> {b.name}</span>;
                      })}
                    </div>
                  ) : <p className="text-muted-foreground text-xs" data-testid="student-badges-empty">No badges earned yet.</p>}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
